import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, X } from 'lucide-react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}
function sameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}
function formatDate(date, fmt = 'DD MMM YYYY') {
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const mon = MONTHS[date.getMonth()].slice(0, 3);
  const y = date.getFullYear();
  return fmt
    .replace('DD', d)
    .replace('MMM', mon)
    .replace('MM', m)
    .replace('YYYY', y);
}

/**
 * DatePicker - Premium calendar date picker
 *
 * Props:
 *  - value: Date | null
 *  - onChange: (date: Date) => void
 *  - placeholder: string
 *  - label: string
 *  - minDate: Date
 *  - maxDate: Date
 *  - disabled: boolean
 *  - clearable: boolean
 *  - format: string (e.g. 'DD MMM YYYY')
 *  - id: string
 *  - className: string
 */
export default function DatePicker({
  value = null,
  onChange,
  placeholder = 'Select date',
  label,
  minDate,
  maxDate,
  disabled = false,
  clearable = false,
  format = 'DD MMM YYYY',
  id = 'date-picker',
  className = '',
}) {
  const today = new Date();
  const initialDate = value || today;

  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());
  const [mode, setMode] = useState('days'); // 'days' | 'months' | 'years'

  const containerRef = useRef(null);

  const close = useCallback(() => { setIsOpen(false); setMode('days'); }, []);

  const open = () => { if (!disabled) setIsOpen(true); };

  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [close]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [close]);

  // Keep view in sync with value
  useEffect(() => {
    if (value) {
      setViewYear(value.getFullYear());
      setViewMonth(value.getMonth());
    }
  }, [value]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const isDisabledDate = (d) => {
    if (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) return true;
    if (maxDate && d > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())) return true;
    return false;
  };

  const selectDay = (day) => {
    const chosen = new Date(viewYear, viewMonth, day);
    if (isDisabledDate(chosen)) return;
    onChange?.(chosen);
    close();
  };

  const selectMonth = (idx) => {
    setViewMonth(idx);
    setMode('days');
  };

  const selectYear = (yr) => {
    setViewYear(yr);
    setMode('months');
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  // Build year range
  const yearRange = Array.from({ length: 21 }, (_, i) => viewYear - 10 + i);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const emptyCells = Array.from({ length: firstDay });
  const dayCells = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <div
      ref={containerRef}
      className={`ui-datepicker ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${className}`}
      id={id}
    >
      {label && <label className="ui-datepicker-label">{label}</label>}

      <button
        type="button"
        className="ui-datepicker-trigger"
        onClick={open}
        disabled={disabled}
        aria-label={label || placeholder}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <CalIcon size={16} className="ui-datepicker-cal-icon" />
        <span className={`ui-datepicker-value ${!value ? 'placeholder' : ''}`}>
          {value ? formatDate(value, format) : placeholder}
        </span>
        {clearable && value && (
          <span className="ui-datepicker-clear" onClick={clear} role="button" aria-label="Clear date">
            <X size={14} />
          </span>
        )}
      </button>

      {isOpen && (
        <div className="ui-datepicker-popup" role="dialog" aria-modal="true" aria-label="Date picker">
          {/* Header */}
          <div className="ui-datepicker-header">
            <button
              type="button"
              className="ui-datepicker-nav-btn"
              onClick={mode === 'years' ? () => setViewYear(y => y - 10) : prevMonth}
              aria-label="Previous"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="ui-datepicker-header-labels">
              <button
                type="button"
                className="ui-datepicker-month-label"
                onClick={() => setMode(mode === 'months' ? 'days' : 'months')}
              >
                {MONTHS[viewMonth]}
              </button>
              <button
                type="button"
                className="ui-datepicker-year-label"
                onClick={() => setMode(mode === 'years' ? 'days' : 'years')}
              >
                {viewYear}
              </button>
            </div>

            <button
              type="button"
              className="ui-datepicker-nav-btn"
              onClick={mode === 'years' ? () => setViewYear(y => y + 10) : nextMonth}
              aria-label="Next"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Days view */}
          {mode === 'days' && (
            <div className="ui-datepicker-body">
              <div className="ui-datepicker-weekdays">
                {DAYS.map(d => <span key={d} className="ui-datepicker-weekday">{d}</span>)}
              </div>
              <div className="ui-datepicker-grid">
                {emptyCells.map((_, i) => <span key={`e${i}`} />)}
                {dayCells.map(day => {
                  const d = new Date(viewYear, viewMonth, day);
                  const isToday = sameDay(d, today);
                  const isSelected = sameDay(d, value);
                  const isDisabled = isDisabledDate(d);
                  return (
                    <button
                      key={day}
                      type="button"
                      className={`ui-datepicker-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => selectDay(day)}
                      disabled={isDisabled}
                      aria-label={formatDate(d, 'DD MMM YYYY')}
                      aria-pressed={isSelected}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Months view */}
          {mode === 'months' && (
            <div className="ui-datepicker-months-grid">
              {MONTHS.map((m, i) => (
                <button
                  key={m}
                  type="button"
                  className={`ui-datepicker-month-item ${i === viewMonth ? 'selected' : ''}`}
                  onClick={() => selectMonth(i)}
                >
                  {m.slice(0, 3)}
                </button>
              ))}
            </div>
          )}

          {/* Years view */}
          {mode === 'years' && (
            <div className="ui-datepicker-years-grid">
              {yearRange.map(yr => (
                <button
                  key={yr}
                  type="button"
                  className={`ui-datepicker-year-item ${yr === viewYear ? 'selected' : ''} ${yr === today.getFullYear() ? 'today' : ''}`}
                  onClick={() => selectYear(yr)}
                >
                  {yr}
                </button>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="ui-datepicker-footer">
            <button type="button" className="ui-datepicker-today-btn" onClick={() => selectDay(today.getDate())}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
